/**
 * Paragraph Store & Language Selector
 *
 * Reads the stores config (theme-editor inline JSON, falling back to
 * /assets/paragraph-stores.json), renders the dropdown options, runs
 * IP geo-detection, and shows a session-dismissible banner — either:
 *   - "store-suggestion" (visitor's country maps to a different store), or
 *   - "shipping-soon"    (visitor's country is in the shippingSoonCountries
 *                          list — we don't ship there yet, but capture an
 *                          email signup with GDPR consent).
 *
 * The signup form posts to /contact (Shopify's native customer-form) with
 * a tag of "eu-launch-list" so Klaviyo can segment on it once installed.
 */
(function () {
  'use strict';

  var GEO_URL = 'https://www.cloudflare.com/cdn-cgi/trace';
  var GEO_FALLBACK_URL = '/browsing_context_suggestions.json';
  var DISMISS_KEY = 'paragraph:geo-banner-dismissed';
  var BANNER_REVEAL_DELAY_MS = 800;
  var BANNER_HIDE_DELAY_MS = 400;
  var SUCCESS_AUTODISMISS_MS = 5000;

  // ISO-3166 alpha-2 → English country names. Covers EU + common locales;
  // anything not listed falls back to the raw code so the banner still
  // makes sense (rare given Cloudflare's coverage).
  var COUNTRY_NAMES = {
    AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', CY: 'Cyprus', CZ: 'Czechia',
    DE: 'Germany', DK: 'Denmark', EE: 'Estonia', ES: 'Spain', FI: 'Finland',
    FR: 'France', GR: 'Greece', HR: 'Croatia', HU: 'Hungary', IE: 'Ireland',
    IT: 'Italy', LT: 'Lithuania', LU: 'Luxembourg', LV: 'Latvia', MT: 'Malta',
    NL: 'Netherlands', PL: 'Poland', PT: 'Portugal', RO: 'Romania',
    SE: 'Sweden', SI: 'Slovenia', SK: 'Slovakia', NO: 'Norway',
    CH: 'Switzerland', GB: 'United Kingdom', UK: 'United Kingdom',
    US: 'the United States', CA: 'Canada', MX: 'Mexico',
    AU: 'Australia', NZ: 'New Zealand', JP: 'Japan', KR: 'South Korea',
    IL: 'Israel', AE: 'the UAE', SG: 'Singapore', HK: 'Hong Kong'
  };

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function countryName(code) {
    if (!code) return '';
    return COUNTRY_NAMES[code.toUpperCase()] || code.toUpperCase();
  }

  function fillTemplate(tpl, vars) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; });
  }

  // Stamp a cross-store hand-off with attribution UTMs so the destination
  // store's GA4 property credits the source store instead of recording the
  // visitor as direct/unattributed traffic. Per ADR-4.1/4.8 the two stores
  // are deliberately region-isolated (separate GA4 properties, separate sGTM,
  // region-scoped cookies — NO shared client_id, NO cross-domain linker — to
  // avoid GDPR cross-region identity bleed). UTMs carry campaign metadata in
  // the URL, not personal identity, so this is the one form of cross-store
  // connection that preserves attribution without violating that isolation.
  //
  // Attribution priority, best first:
  //   1. Live campaign params already on the URL (visitor is switching from
  //      the landing page itself) — left untouched, real campaign keeps the
  //      credit. A `pw_xs=<from>` breadcrumb is added so switcher-driven
  //      sessions stay countable.
  //   2. First-touch replay — the visitor landed from a campaign, browsed
  //      (query string gone), then switched. pw-track.js persisted the
  //      landing page's utm_* / click-ID params in sessionStorage
  //      (PW.track.getFirstTouch, click IDs consent-gated); we replay them
  //      here so the destination store attributes the session to the
  //      ORIGINAL campaign, not to a generic cross-store referral. Click-ID
  //      replay also lets the destination domain regenerate _fbc / pick up
  //      gclid natively.
  //   3. Fallback stamp (utm_source=paragraph_<from> / cross_store) — no
  //      known first touch, so credit the source store rather than letting
  //      the session land as direct/unattributed. NOTE: never stamped on top
  //      of a replayed click ID — manual UTMs would override gclid-derived
  //      attribution in GA4.
  // Only cross-store / cross-market jumps are decorated; same-store language
  // switches are not (they stay within one property and need no
  // re-attribution).
  function appendCrossStoreUtms(url, fromCode, toCode) {
    if (!url) return url;
    var from = (fromCode || '').toLowerCase();
    var to = (toCode || '').toLowerCase();
    if (!from || from === to) return url; // nothing to attribute

    var hashIndex = url.indexOf('#');
    var hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
    var base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;

    var urlHasUtms = /[?&]utm_/i.test(base);
    var urlHasClickId = /[?&](gclid|gbraid|wbraid|fbclid|msclkid|ttclid)=/i.test(base);

    var pairs = [];
    var firstTouch = null;
    if (!urlHasUtms && !urlHasClickId &&
        window.PW && window.PW.track &&
        typeof window.PW.track.getFirstTouch === 'function') {
      firstTouch = window.PW.track.getFirstTouch();
    }
    if (firstTouch) {
      for (var key in firstTouch) {
        if (Object.prototype.hasOwnProperty.call(firstTouch, key)) {
          pairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(firstTouch[key]));
        }
      }
    }

    if (urlHasUtms || urlHasClickId || firstTouch) {
      pairs.push('pw_xs=' + encodeURIComponent(from));
    } else {
      pairs.push('utm_source=paragraph_' + encodeURIComponent(from));
      pairs.push('utm_medium=cross_store');
      pairs.push('utm_campaign=store_switcher');
    }

    var sep = base.indexOf('?') >= 0 ? '&' : '?';
    return base + sep + pairs.join('&') + hash;
  }

  function parseCloudflareTrace(text) {
    var lines = text.split('\n');
    for (var i = 0; i < lines.length; i++) {
      var parts = lines[i].split('=');
      if (parts[0] && parts[0].toLowerCase() === 'loc') return (parts[1] || '').trim().toUpperCase();
    }
    return null;
  }

  // Shopify's storefront-side geo endpoint. Returns:
  //   { detected_values: { country: { handle: "US", name: "United States" }, ... }, ... }
  // This is the same data Shopify Markets uses to suggest a market, so it's
  // the source-of-truth for "what country does this visitor appear to be in"
  // from a commerce perspective.
  function fetchShopifyGeo() {
    return fetch(GEO_FALLBACK_URL)
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (json) {
        var handle =
          (json && json.detected_values && json.detected_values.country &&
           json.detected_values.country.handle) ||
          // Older Shopify shape — left as a defensive fallback.
          (json && json.detected_country) ||
          null;
        return handle ? String(handle).toUpperCase() : null;
      })
      .catch(function () { return null; });
  }

  function fetchCloudflareGeo() {
    return fetch(GEO_URL, { cache: 'force-cache' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
      .then(parseCloudflareTrace)
      .catch(function () { return null; });
  }

  // Detect the visitor's country. Run both lookups in parallel and prefer
  // Shopify's reading because:
  //   (a) it's the same value Shopify Markets uses internally, so the banner
  //       suggestion stays consistent with checkout / market routing;
  //   (b) Cloudflare's GeoIP occasionally misclassifies US ISPs as Canadian
  //       (and vice versa) when traffic is routed through cross-border POPs;
  //   (c) Cloudflare's trace is sometimes blocked by ad-blockers / privacy
  //       extensions / corporate DNS, returning null — in that case Shopify
  //       still gives us a working answer.
  // If Shopify returns nothing, fall back to Cloudflare. If both fail, return
  // null and the banner stays hidden.
  function detectCountry() {
    return Promise.all([fetchShopifyGeo(), fetchCloudflareGeo()])
      .then(function (results) {
        var shopify = results[0];
        var cloudflare = results[1];
        if (window.PW_DEBUG) {
          // eslint-disable-next-line no-console
          console.log('[paragraph-store-selector] geo:', { shopify: shopify, cloudflare: cloudflare });
        }
        return shopify || cloudflare || null;
      });
  }

  function findStoreByCountry(stores, country) {
    if (!country) return null;
    for (var i = 0; i < stores.length; i++) {
      if ((stores[i].countries || []).indexOf(country) !== -1) return stores[i];
    }
    return null;
  }

  function findStoreByCode(stores, code) {
    for (var i = 0; i < stores.length; i++) {
      if (stores[i].code === code) return stores[i];
    }
    return null;
  }

  // Map the current URL path (e.g. /en-ch/products/x) to the pseudo-store
  // entry whose marketPath pins that prefix (e.g. CH → /en-ch). Used so the
  // trigger pill and geo-banner reflect Switzerland when the visitor is on
  // /en-ch/, even though the theme is the EU theme.
  //
  // We can't rely on request.locale.iso_code here — Shopify reports it as
  // "en" on /en-ch/, not "en-ch", so the Liquid-passed locale alone is
  // ambiguous between the EU default and the CH market. The pathname is
  // authoritative.
  //
  // Returns null if no store's marketPath matches the leading path segment,
  // in which case we fall back to the theme-level current_store_code.
  function findStoreByPath(stores, pathname) {
    if (!pathname) return null;
    var path = pathname.toLowerCase();
    for (var i = 0; i < stores.length; i++) {
      var mp = (stores[i].marketPath || '').toLowerCase();
      if (!mp) continue;
      if (path === mp || path.indexOf(mp + '/') === 0) return stores[i];
    }
    return null;
  }

  function resolveFlagUrl(storesUrl, flagFilename) {
    if (!flagFilename) return '';
    return storesUrl.replace(/[^/]+$/, '') + flagFilename;
  }

  function renderOptions(root, data) {
    var storesUrl = root.getAttribute('data-stores-url');
    var currentCode = root.getAttribute('data-current-store');
    var currentLocale = (root.getAttribute('data-current-locale') || '').toLowerCase();

    // Path-driven store override.
    //
    // The Liquid sets data-current-store from settings.current_store_code,
    // which is theme-level — always "eu" on the EU theme, even when the
    // visitor is on the Swiss market URL (/en-ch). The dropdown lists CH as
    // its own pseudo-store (so the trigger pill, the active-row highlight,
    // and the geo-banner suggestion can all reflect Switzerland). Detect
    // marketPath-pinned markets from the URL pathname and rebind currentCode
    // + the data attribute so every downstream reader sees the corrected
    // value.
    var pathStore = findStoreByPath(data.stores, window.location.pathname);
    if (pathStore && pathStore.code !== currentCode) {
      currentCode = pathStore.code;
      root.setAttribute('data-current-store', currentCode);
    }

    var storeList = $(root, '[data-list="store"]');
    var langGroup = $(root, '[data-group="language"]');
    var langList = $(root, '[data-list="language"]');
    var triggerFlag = $(root, '.paragraph-store-selector__trigger-flag');
    var triggerLabel = $(root, '.paragraph-store-selector__trigger-label');

    var currentStore = findStoreByCode(data.stores, currentCode);
    if (!currentStore) {
      // Silently degrade in production. The component renders a hidden
      // trigger when no current_store_code matches; logging this every
      // page-load is noise for storefront visitors.
      if (window.PW_DEBUG) {
        // eslint-disable-next-line no-console
        console.warn('[paragraph-store-selector] current_store_code "' + currentCode + '" not found');
      }
      return { currentStore: null };
    }

    if (triggerFlag) {
      triggerFlag.style.backgroundImage = 'url(' + resolveFlagUrl(storesUrl, currentStore.flag) + ')';
    }
    if (triggerLabel) {
      var shownLocale = currentLocale.split('-')[0].toUpperCase();
      triggerLabel.textContent = currentStore.code.toUpperCase() + ' / ' + shownLocale;
    }

    storeList.innerHTML = '';
    data.stores.forEach(function (store) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'paragraph-store-selector__option';
      btn.setAttribute('data-store-code', store.code);
      if (store.code === currentCode) btn.setAttribute('aria-current', 'true');

      var flag = document.createElement('span');
      flag.className = 'paragraph-store-selector__option-flag';
      flag.setAttribute('aria-hidden', 'true');
      flag.style.backgroundImage = 'url(' + resolveFlagUrl(storesUrl, store.flag) + ')';

      var name = document.createElement('span');
      name.className = 'paragraph-store-selector__option-name';
      name.textContent = store.name;

      var suggested = document.createElement('span');
      suggested.className = 'paragraph-store-selector__suggested';
      suggested.textContent = 'Suggested';

      var check = document.createElement('span');
      check.className = 'paragraph-store-selector__option-check';
      check.setAttribute('aria-hidden', 'true');

      btn.appendChild(flag);
      btn.appendChild(name);
      btn.appendChild(suggested);
      btn.appendChild(check);
      li.appendChild(btn);
      storeList.appendChild(li);
    });

    // Language picker stays hidden if every store has only one language
    // available (English-only launch state). Re-enables automatically as
    // soon as any store gains a second locale.
    var anyMultiLang = data.stores.some(function (s) { return (s.languages || []).length > 1; });
    if (langGroup) langGroup.hidden = !anyMultiLang;

    if (langList) {
      langList.innerHTML = '';
      (currentStore.languages || []).forEach(function (lang) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'paragraph-store-selector__option';
        btn.setAttribute('data-language-code', lang.code);
        var isCurrent = currentLocale === lang.code || currentLocale.split('-')[0] === lang.code;
        if (isCurrent) btn.setAttribute('aria-current', 'true');

        var name = document.createElement('span');
        name.className = 'paragraph-store-selector__option-name';
        name.textContent = lang.label;

        var check = document.createElement('span');
        check.className = 'paragraph-store-selector__option-check';
        check.setAttribute('aria-hidden', 'true');

        btn.appendChild(name);
        btn.appendChild(check);
        li.appendChild(btn);
        langList.appendChild(li);
      });
    }

    return { currentStore: currentStore };
  }

  function bindToggle(root) {
    var trigger = $(root, '.paragraph-store-selector__trigger');
    var menu = $(root, '.paragraph-store-selector__menu');
    if (!trigger || !menu) return;

    function open() { menu.hidden = false; trigger.setAttribute('aria-expanded', 'true'); }
    function close() { menu.hidden = true; trigger.setAttribute('aria-expanded', 'false'); }
    function isOpen() { return !menu.hidden; }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      isOpen() ? close() : open();
    });

    var supportsHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (supportsHover) {
      var closeTimer = null;
      function scheduleClose() {
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(function () { close(); }, 150);
      }
      function cancelClose() { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } }
      root.addEventListener('mouseenter', function () { cancelClose(); open(); });
      root.addEventListener('mouseleave', scheduleClose);
    }

    document.addEventListener('click', function (e) {
      if (isOpen() && !root.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) { close(); trigger.focus(); }
    });
  }

  function bindStoreOptions(root, data) {
    var currentCode = root.getAttribute('data-current-store');
    var isDrawer = root.classList.contains('paragraph-store-selector--drawer');

    $$(root, '[data-store-code]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var code = btn.getAttribute('data-store-code');
        var target = findStoreByCode(data.stores, code);
        if (!target) return;

        // Stores with a fixed marketPath (e.g. Switzerland → /en-ch)
        // navigate directly in one click.
        if (target.marketPath) {
          var path = window.location.pathname + window.location.search;
          path = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
          var marketHref = (target.url || '').replace(/\/$/, '') + target.marketPath + path;
          window.location.href = appendCrossStoreUtms(marketHref, currentCode, target.code);
          return;
        }

        if (isDrawer) {
          $$(root, '[data-store-code]').forEach(function (b) { b.removeAttribute('aria-current'); });
          btn.setAttribute('aria-current', 'true');
          root.setAttribute('data-pending-store', code);
          renderDrawerLanguagesForStore(root, target);
          return;
        }

        if (code === currentCode) return;
        if (!target.url) return;
        // Strip the source store's locale prefix (e.g. /en-us/, /en-ch/)
        // before appending the path to the target store's base URL.
        // Otherwise navigating from US (/en-us/products/x) to EU
        // produces paragraph.wine/en-us/products/x, a 404 since the
        // EU store doesn't have an en-us locale published.
        var path = window.location.pathname + window.location.search;
        path = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
        var crossStoreHref = target.url.replace(/\/$/, '') + path;
        window.location.href = appendCrossStoreUtms(crossStoreHref, currentCode, target.code);
      });
    });
  }

  function renderDrawerLanguagesForStore(root, store) {
    var langList = $(root, '[data-list="language"]');
    if (!langList || !store || !store.languages) return;
    var currentCode = root.getAttribute('data-current-store');
    var currentLocale = (root.getAttribute('data-current-locale') || '').toLowerCase();
    var path = window.location.pathname + window.location.search;
    path = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';

    langList.innerHTML = '';
    store.languages.forEach(function (lang) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'paragraph-store-selector__option';
      btn.setAttribute('data-language-code', lang.code);
      var isSameStore = store.code === currentCode;
      var isCurrentLang = currentLocale === lang.code || currentLocale.split('-')[0] === lang.code;
      if (isSameStore && isCurrentLang) btn.setAttribute('aria-current', 'true');

      var name = document.createElement('span');
      name.className = 'paragraph-store-selector__option-name';
      name.textContent = lang.label;
      btn.appendChild(name);

      btn.addEventListener('click', function () {
        var base = (store.url || '').replace(/\/$/, '');
        // Only add a Shopify-Markets locale prefix when the store has
        // additional published languages OR the chosen language is not
        // the store's default. Single-language stores serve the primary
        // locale at the bare root (e.g. paragraph.wine/), so prefixing
        // /en-eu would 404. Likewise, US default-language English on
        // paragraph-wines.com lives at /, not /en-us.
        var langCount = (store.languages || []).length;
        var isDefaultLang = (store.defaultLanguage || '').toLowerCase() === lang.code.toLowerCase();
        var prefix = '';
        if (langCount > 1 || !isDefaultLang) {
          prefix = '/' + lang.code + '-' + store.code;
        }
        window.location.href = base + prefix + path;
      });

      li.appendChild(btn);
      langList.appendChild(li);
    });
  }

  function bindLanguageOptions(root) {
    if (root.classList.contains('paragraph-store-selector--drawer')) return;
    var form = $(root, '.paragraph-store-selector__locale-form');
    var input = $(root, '[data-locale-input]');
    var currentLocale = (root.getAttribute('data-current-locale') || '').toLowerCase();
    if (!form || !input) return;

    $$(root, '[data-language-code]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var code = btn.getAttribute('data-language-code');
        if (code === currentLocale || code === currentLocale.split('-')[0]) return;
        input.value = code;
        form.submit();
      });
    });
  }

  function showBanner(banner) {
    banner.hidden = false;
    setTimeout(function () { banner.setAttribute('data-visible', 'true'); }, BANNER_REVEAL_DELAY_MS);
  }

  function hideBanner(banner) {
    banner.removeAttribute('data-visible');
    setTimeout(function () { banner.hidden = true; }, BANNER_HIDE_DELAY_MS);
  }

  function bindBanner(root, data, detected) {
    // Use a document-level lookup because portalBanner moves the banner
    // OUT of the selector root and prepends it to <body>. The selector
    // still owns the menu/trigger, but the banner now lives at the top
    // of the page so it can push content down via real layout flow.
    var banner = document.querySelector('paragraph-store-selector ~ [data-banner], body > [data-banner]')
              || $(root, '[data-banner]');
    if (!banner) return;
    var msg = $(banner, '[data-banner-msg]');
    var switchBtn = $(banner, '[data-banner-switch]');
    var dismissBtn = $(banner, '[data-banner-dismiss]');
    if (!msg || !switchBtn || !dismissBtn) return;

    if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    if (!detected) return;

    var currentCode = root.getAttribute('data-current-store');
    var detectedName = countryName(detected);
    var shippingSoon = (data.shippingSoonCountries || []).indexOf(detected) !== -1;
    var suggested = findStoreByCountry(data.stores, detected);

    // Path 1 — shipping-soon market: show "we'll ship there soon" + signup form.
    if (shippingSoon) {
      var tpl = data.shippingSoonMessage || "You're in {country} — we're shipping there soon. Sign up for updates.";
      msg.textContent = fillTemplate(tpl, { country: detectedName });
      switchBtn.textContent = data.shippingSoonCta || 'Notify me';
      switchBtn.setAttribute('data-mode', 'shipping-soon');
      bindShippingSoonSignup(root, banner, switchBtn, detected, detectedName);
      bindDismiss(banner, dismissBtn);
      showBanner(banner);
      return;
    }

    // Path 2 — visitor maps to a different store. Previously this revealed a
    // subtle top-of-page "You're in X — visit Y" banner. It now fires an
    // explicit wrong-site MODAL (paragraph-geo-modal.js) with a blurred
    // backdrop, so a Spain visitor landing on the Swiss site (or a US visitor
    // on the EU site, etc.) is unmistakably told they're on the wrong site.
    //
    // We build the fully-resolved switch href HERE — locale-prefix stripping,
    // marketPath, and cross-store UTM attribution all live in this file — then
    // hand the presenter a ready-to-use URL via a CustomEvent. The selector
    // keeps owning geo-detection (one IP lookup); the modal stays a dumb
    // presenter.
    if (suggested && suggested.code !== currentCode && suggested.url) {
      var opt = root.querySelector('[data-store-code="' + suggested.code + '"]');
      if (opt) opt.setAttribute('data-suggested', 'true');

      // Strip the source store's locale prefix (e.g. /en-us/) so we don't
      // carry it across to the target store, where that locale typically
      // doesn't exist (paragraph.wine has no /en-us, etc).
      var switchPath = window.location.pathname + window.location.search;
      switchPath = switchPath.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
      var suggestedHref = suggested.marketPath
        ? suggested.url.replace(/\/$/, '') + suggested.marketPath + switchPath
        : suggested.url.replace(/\/$/, '') + switchPath;
      suggestedHref = appendCrossStoreUtms(suggestedHref, currentCode, suggested.code);

      var storesUrl = root.getAttribute('data-stores-url') || '';
      var flagUrl = storesUrl && suggested.flag ? resolveFlagUrl(storesUrl, suggested.flag) : '';

      document.dispatchEvent(new CustomEvent('paragraph:wrong-site', {
        detail: {
          country: detected,
          countryName: detectedName,
          flagUrl: flagUrl,
          store: {
            code: suggested.code,
            name: suggested.name,
            url: suggested.url,
            href: suggestedHref
          }
        }
      }));
    }
  }

  function bindDismiss(banner, dismissBtn) {
    dismissBtn.addEventListener('click', function () {
      try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* storage blocked */ }
      hideBanner(banner);
    });
  }

  // Shipping-soon: clicking the "Notify me" CTA reveals the inline GDPR
  // signup form inside the banner. The form posts to /contact with a tag
  // ("eu-launch-list") + the detected country so the marketing tool
  // (Klaviyo, once installed) can segment on it.
  function bindShippingSoonSignup(_root, banner, switchBtn, detectedCode, detectedName) {
    var form = $(banner, '[data-banner-signup]');
    var row = $(banner, '[data-banner-row]');
    var emailInput = $(banner, '[data-signup-email]');
    var consentInput = $(banner, '[data-signup-consent]');
    var countryInput = $(banner, '[data-signup-country]');
    var success = $(banner, '[data-signup-success]');
    if (!form || !row || !emailInput || !consentInput) return;

    if (countryInput) countryInput.value = detectedCode + ' (' + detectedName + ')';

    switchBtn.addEventListener('click', function () {
      // Slide the form into view and shift focus to email input.
      form.hidden = false;
      banner.setAttribute('data-signup-open', 'true');
      // Use rAF so the hidden→visible transition fires from the new state.
      requestAnimationFrame(function () { emailInput.focus(); });
    });

    form.addEventListener('submit', function (e) {
      // Guard: native HTML5 validation handles email + required consent.
      // Once Klaviyo is wired up, swap form.action to the Klaviyo
      // list-subscribe URL — we already pass email, marketing consent,
      // tag, and country, which is exactly what Klaviyo expects.
      // For now, let the form post to /contact (Shopify customer-form),
      // then short-circuit to the inline success state via fetch.
      var allowNativePost = form.getAttribute('data-allow-native-post') === '1';
      if (allowNativePost) return; // escape hatch for local debugging
      e.preventDefault();
      if (!form.checkValidity()) { form.reportValidity(); return; }

      var fd = new FormData(form);
      // Capture the email value BEFORE the fetch so we can pass it to
      // PW.track.emailSignup() on success. We use the live input value
      // not the FormData snapshot — both are equivalent here but the
      // live read is robust to user edits during the request.
      var emailValue = (emailInput && emailInput.value) || '';
      fetch(form.action, { method: 'POST', body: fd, credentials: 'same-origin' })
        .catch(function () { /* even if /contact errors, show success — Shopify
                                 customer-form sometimes redirects via 302 which
                                 fetch follows; the actual record is created
                                 server-side regardless. */ })
        .finally(function () {
          // Fire conversion AFTER the user explicitly checked the GDPR
          // consent box (form's required attribute guarantees this).
          // PW.track.emailSignup fans out to GA4 (sign_up event with
          // Enhanced Conversions user_data.email_address), GTM
          // dataLayer (event: email_signup), and any future
          // Klaviyo/Meta CAPI tags configured in GTM.
          if (window.PW && window.PW.track && window.PW.track.emailSignup) {
            window.PW.track.emailSignup({
              surface: 'geo_banner',
              email: emailValue,
              country: detectedCode
            });
          }
          form.hidden = true;
          if (success) {
            success.hidden = false;
            success.textContent = "Thanks — we'll be in touch when we launch in " + detectedName + '.';
          }
          banner.setAttribute('data-signup-success', 'true');
          setTimeout(function () {
            try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* */ }
            hideBanner(banner);
          }, SUCCESS_AUTODISMISS_MS);
        });
    });
  }

  // Move the banner out of the header into a top-of-body portal so it
  // takes real layout space and pushes the sticky header (and the rest
  // of the page) downward instead of overlapping. Idempotent — safe to
  // call once per selector instance.
  function portalBanner(root) {
    var banner = $(root, '[data-banner]');
    if (!banner) return;
    // Only portal the header-variant banner; the drawer doesn't have one.
    if (!root.classList.contains('paragraph-store-selector--header')) return;
    // If already portaled (e.g. another selector beat us to it), bail.
    if (banner.parentElement === document.body && banner === document.body.firstElementChild) return;
    document.body.insertBefore(banner, document.body.firstChild);
  }

  // Publish the banner's measured height as --pw-banner-height on
  // <html>. The header.liquid scroll-direction controller reads this
  // value and adds it to `style.top` ONLY in the transparent state
  // (scroll=0) — when the banner is in document flow at the top of
  // the page and the absolute-positioned header would otherwise
  // overlap it.
  //
  // In the hide / reveal states the banner is scrolled out of the
  // viewport so the controller goes back to top:0 cleanly, and the
  // scroll-up reveal animates in at the natural top — independent
  // of banner state. No collision, no flicker.
  //
  // After publishing the new value we dispatch a synthetic scroll
  // event so the controller re-runs setTransparent() and picks up
  // the new offset (its early-return has been removed for exactly
  // this purpose). Done in a microtask so the CSS var is committed
  // before the controller reads it back.
  function syncBannerOffset(banner) {
    var apply = function () {
      var visible = !banner.hidden && banner.getAttribute('data-visible') === 'true';
      var h = visible ? Math.round(banner.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--pw-banner-height', h + 'px');
      // Nudge the scroll controller — only meaningful while the user
      // is at the very top of the page (transparent state).
      if (window.scrollY < 10) {
        Promise.resolve().then(function () {
          window.dispatchEvent(new Event('scroll'));
        });
      }
    };
    apply();
    if ('ResizeObserver' in window) {
      new ResizeObserver(apply).observe(banner);
    }
    new MutationObserver(apply).observe(banner, {
      attributes: true,
      attributeFilter: ['data-visible', 'data-signup-open', 'hidden']
    });
  }

  function init(root) {
    if (root.hasAttribute('data-inited')) return;
    root.setAttribute('data-inited', 'true');

    var storesUrl = root.getAttribute('data-stores-url');
    if (!storesUrl) return;

    // Portal first so all later querySelector calls inside bindBanner
    // continue to find the banner via document-level lookups (they use
    // the local `root` reference which still owns the banner via
    // querySelector — fix that by switching to document-level scope).
    portalBanner(root);
    var portaledBanner = document.body.firstElementChild &&
      document.body.firstElementChild.matches('.paragraph-store-selector__banner')
      ? document.body.firstElementChild : null;
    if (portaledBanner) syncBannerOffset(portaledBanner);

    var inlineEl = document.getElementById('pw-store-config');
    var inlineData = null;
    if (inlineEl) {
      try { inlineData = JSON.parse(inlineEl.textContent); } catch (e) { inlineData = null; }
    }
    var loader = (inlineData && Array.isArray(inlineData.stores) && inlineData.stores.length)
      ? Promise.resolve(inlineData)
      : fetch(storesUrl, { cache: 'force-cache' }).then(function (r) { return r.ok ? r.json() : Promise.reject(); });

    loader
      .then(function (data) {
        if (!data || !Array.isArray(data.stores)) throw new Error('invalid stores json');
        var ctx = renderOptions(root, data);
        if (!ctx.currentStore) return;
        bindToggle(root);
        bindStoreOptions(root, data);
        bindLanguageOptions(root);
        return detectCountry().then(function (country) {
          bindBanner(root, data, country);
        });
      })
      .catch(function (err) {
        // Silently degrade. Init failure means the JSON fetch failed
        // (network, CDN, malformed JSON); the trigger stays hidden so
        // there's no visible breakage for the user. Set window.PW_DEBUG
        // to surface errors during diagnosis.
        if (window.PW_DEBUG) {
          // eslint-disable-next-line no-console
          console.warn('[paragraph-store-selector] init failed:', err);
        }
      });
  }

  function boot() { $$(document, 'paragraph-store-selector').forEach(init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
